import {
  AlertTriangle,
  CircleOff,
  CloudOff,
  FilterX,
  LoaderCircle,
  NotebookPen,
  ShieldAlert,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

import { copy } from './copy';

export type StatePanelKind =
  | 'loading'
  | 'empty-first-use'
  | 'empty-filtered'
  | 'partial'
  | 'offline'
  | 'error-recoverable'
  | 'error-terminal'
  | 'destructive-confirm';

export type StatePanelAction = {
  label: string;
  onSelect: () => void;
  tone?: 'primary' | 'neutral' | 'danger';
};

export type StatePanelProps = {
  kind: StatePanelKind;
  title?: string;
  description?: ReactNode;
  action?: StatePanelAction;
  secondaryAction?: StatePanelAction;
  affectedCount?: number;
  preservedDataMessage?: string;
  referenceId?: string;
};

type Icon = ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>;

const icons: Record<StatePanelKind, Icon> = {
  loading: LoaderCircle,
  'empty-first-use': NotebookPen,
  'empty-filtered': FilterX,
  partial: CloudOff,
  offline: CloudOff,
  'error-recoverable': AlertTriangle,
  'error-terminal': CircleOff,
  'destructive-confirm': ShieldAlert,
};

const defaults: Record<StatePanelKind, { title: string; description: string }> = {
  loading: {
    title: copy.state.loadingTitle,
    description: copy.state.loadingDescription,
  },
  'empty-first-use': {
    title: '첫 기록을 시작해 볼까요?',
    description: copy.log.prompt,
  },
  'empty-filtered': {
    title: copy.state.emptyFilteredTitle,
    description: copy.state.emptyFilteredDescription,
  },
  partial: {
    title: copy.state.partialTitle,
    description: copy.state.partialDescription,
  },
  offline: {
    title: copy.state.offlineTitle,
    description: copy.state.offlineDescription,
  },
  'error-recoverable': {
    title: copy.state.recoverableTitle,
    description: '잠시 뒤 다시 시도하거나 다른 방법을 선택할 수 있어요.',
  },
  'error-terminal': {
    title: copy.state.terminalTitle,
    description: '오류 참조 번호와 함께 지원을 요청해 주세요.',
  },
  'destructive-confirm': {
    title: copy.state.destructiveTitle,
    description: '영향받는 기록과 되돌릴 수 있는 기간을 확인한 뒤 진행해 주세요.',
  },
};

function ActionButton({ action }: { action: StatePanelAction }) {
  return (
    <button
      className="pn-button"
      data-tone={action.tone ?? 'neutral'}
      onClick={action.onSelect}
      type="button"
    >
      {action.label}
    </button>
  );
}

export function StatePanel({
  kind,
  title,
  description,
  action,
  secondaryAction,
  affectedCount,
  preservedDataMessage,
  referenceId,
}: StatePanelProps) {
  const Icon = icons[kind];
  const fallback = defaults[kind];
  const isLoading = kind === 'loading';

  return (
    <section
      aria-atomic={isLoading ? 'true' : undefined}
      aria-live={isLoading ? 'polite' : undefined}
      className="pn-state-panel"
      data-state={kind}
      role={isLoading ? 'status' : undefined}
    >
      <span className="pn-state-panel__icon" data-animated={isLoading || undefined}>
        <Icon aria-hidden size={24} strokeWidth={1.8} />
      </span>
      <div className="pn-state-panel__content">
        <h2>{title ?? fallback.title}</h2>
        <p>{description ?? fallback.description}</p>
        {affectedCount === undefined ? null : (
          <p className="pn-state-panel__impact">
            영향받는 기록 <strong>{affectedCount.toLocaleString('ko-KR')}개</strong>
          </p>
        )}
        {preservedDataMessage ? (
          <p className="pn-state-panel__preserved">{preservedDataMessage}</p>
        ) : null}
        {referenceId ? (
          <p className="pn-state-panel__reference">오류 참조 번호: {referenceId}</p>
        ) : null}
      </div>
      {action || secondaryAction ? (
        <div className="pn-state-panel__actions">
          {action ? <ActionButton action={action} /> : null}
          {secondaryAction ? <ActionButton action={secondaryAction} /> : null}
        </div>
      ) : null}
    </section>
  );
}
